import { Button, Spinner } from '@gsa/afp-component-library';
import './contracting-review-actions.scss';
import { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import ContractingReviewVendor from './vendor-form/contracting-review-vendor';
import { useSelector } from 'react-redux';
import { TRANSACTION_TYPES } from '../../../../../utilities/CompareVehicleUtils';
import { useCreateVendorQuote, useDeleteVendorQuote, useGetActiveContractsNow, useGetCalculatedPriceNow } from '../../../../../requests/leasing';
import { useDispatch } from 'react-redux';
import { setActiveContracts, setVendorQuotes } from '../../../../../reducers/leasing';
import { applyFormErrorsRecursively } from '@gsa/afp-shared-form-utils';
import { RequisitionStatus } from '../../../../ReviewDetails/RequisitionDetailsUtils';
import { UserRoles } from '../../../../../constants/index.jsx';
import { useCurrentUser } from '@gsa/afp-shared-ui-utils';

export const AREQVendorQuoteAttachments = {
  req_vendor_supporting_doc: 'Vendor supporting document',
  req_vendor_quote: 'Vendor quote',
};

function indexToAlphabet(index) {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    return alphabet[index] || '';
}

export default function ContractingReviewActions({ vendorQuotesSubmit }) {
    const dispatch = useDispatch();
    const [createVendorQuote, { loading: loadingCreateVendorQuote }] = useCreateVendorQuote();
    const requisitionId = useSelector((state) => state.leasingReducer.requisitionId);
    const requisitionStatus = useSelector((state) => state.leasingReducer.requisitionStatus);
    const allAttachmentTypes = useSelector((state) => state.leasingReducer.attachmentTypes);
    const standardItemId = useSelector((state) => state.leasingReducer.selectedStandardItem?.id);
    const quantity = useSelector((state) => state.leasingReducer.vehicleQuantity);
    const vendorQuotes = useSelector((state) => state.leasingReducer.vendorQuotes) || [];
    const [formRefs, setFormRefs] = useState(vendorQuotes.map(() => createRef()));
    const [deleteVendorQuote, { loading: loadingDeleteVendorQuote }] = useDeleteVendorQuote();
    const selectedOptions = (useSelector((state) => state.leasingReducer.compareSelect?.selectedOptions) || []).filter(({ optionCode }) => optionCode !== 'AREQ')
        .map(({ optionCode }) => {
            return { optionCode };
        })
    const { currentUser } = useCurrentUser();
    const isAdmin = currentUser?.roles.some(
      (role) => role.name === UserRoles.SITE_ADMIN,
    );
    const isOrderingAdmin = currentUser?.roles.some(
      (role) => role.name === UserRoles.ORDERING_ADMIN,
    );
    const isContractingOfficer = currentUser?.roles?.some(
      (role) => role.name === UserRoles.CONTRACTING_OFFICER,
    );
    const { data: contractCostBreakdown, loading: loadingContractCostBreakdown } = useGetCalculatedPriceNow({
        skip: !standardItemId || !quantity || !selectedOptions,
        variables: {
            standardItemId: parseFloat(standardItemId),
            quantity,
            selectedOptions,
            // ...(colorPriceInfo && { colorPriceInfo }),
            transactionType: TRANSACTION_TYPES.LEASING,
        }
    });

    const { data: activeSinContracts, loading: loadingActiveSinContracts } = useGetActiveContractsNow({
        standardItemId,
        transactionType: TRANSACTION_TYPES.LEASING,
    });

    useEffect(() => {
        dispatch(setActiveContracts(activeSinContracts || []));
    }, [activeSinContracts]);

    const makeModelOptionMap = activeSinContracts?.reduce((acc, { contract, makeCode, modelName }) => {
        if (!acc[contract.vendor.id]) {
            acc[contract.vendor.id] = [];
        }

        if (!acc[contract.vendor.id].some((option) => option.value === contract.id)) {
            acc[contract.vendor.id].push({
                value: contract.id,
                label: [makeCode, modelName].filter(Boolean).join(' / '),
            });
        }

        return acc;
    }, {});

    const vendors = useMemo(() => {
        if (!activeSinContracts || activeSinContracts.length === 0) return [];
        return activeSinContracts.map((contract) => contract.contract.vendor).reduce((acc, vendor) => {
            if (!acc.some((v) => v.id === vendor.id)) acc.push(vendor);
            return acc;
        }, []);
    }, [activeSinContracts]);

    const attachmentTypes = !allAttachmentTypes ? [] : allAttachmentTypes.filter((type) => {
        return Object.keys(AREQVendorQuoteAttachments).includes(type.attachmentType);
    }).map((type) => ({
        value: type.requisitionAttachmentTypeId,
        label: AREQVendorQuoteAttachments[type.attachmentType],
    }));
    
    const handleAddVendorQuote = () => {
        createVendorQuote({
            variables: {
                requisitionId,
            }
        }).then(({ data: quote }) => {
            const newVendorQuotes = [...vendorQuotes, quote];
            dispatch(setVendorQuotes(newVendorQuotes));

            setFormRefs((prevRefs) => {
                const newRefs = [...prevRefs];
                newRefs.push(createRef());
                return newRefs;
            });
        }).catch((error) => {
            console.error('Error creating vendor quote:', error);
        });
    }

    const handleRemoveVendorQuote = useCallback(async (id) => {
        try {
            const { data: deleted } = await deleteVendorQuote({
                variables: {
                    requisitionVendorQuoteId: parseInt(id),
                }
            })

            if (deleted) {
            dispatch(setVendorQuotes(vendorQuotes.filter(quote => quote.requisitionVendorQuoteId !== id)));
            setFormRefs((prevRefs) => {
                const newRefs = [...prevRefs];
                const indexToRemove = vendorQuotes.findIndex((quote) => quote.requisitionVendorQuoteId === id);
                if (indexToRemove !== -1) {
                newRefs.splice(indexToRemove, 1);
                }
                return newRefs;
            });
            }
        } catch (error) {
            console.error('Error removing additional requirement:', error);
        }
    }, [vendorQuotes]);

    useEffect(() => {
        if (vendorQuotes.length === 0) {
            handleAddVendorQuote();
        }
    }, [])

    const handleContractingReviewSubmit = useCallback(async () => {
        if (formRefs.length === 0) {
            return false;
        }

        const vendorQuoteData = [];
        let hasErrors = false;

        for (let i = 0; i < formRefs.length; i++) {
            const formRef = formRefs[i];
            const { values, errors } = await formRef.current.getValidatedValues(true);
            const errorKeys = Object.keys(errors);
            if (errorKeys.length > 0) {
                for (const key of errorKeys) {
                    applyFormErrorsRecursively(key, errors, formRef.current.setError, formRef.current.clearErrors);
                }

                hasErrors = true;
                continue;
            }

            const contract = activeSinContracts.find((contract) => contract.contract.id === values[`makeModel-${i}`]);

            if (!contract) return false;

            vendorQuoteData.push({
                id: values[`id-${i}`],
                contractLineId: contract.id,
                contractingOfficerComments: values[`contractingOfficerComments-${i}`],
                extendedShippingDays: values[`extendedShippingDays-${i}`],
                makeCode: contract.makeCode,
                makeModel: [contract.makeCode, contract.modelName].filter(Boolean).join(' / '),
                totalPrice: values[`areqPricePerVehicle-${i}`],
                vendorId: contract.contract.vendor.id,
                vendorName: contract.contract.vendor.vendorName,
            })
        };

        if (hasErrors) return false;

        return {
          contractingReviewInput: {
            requisitionId,
            requisitionQuotes: vendorQuoteData,
          }
        }
    }, [formRefs, activeSinContracts]);

    const handleEngineeringApprovalSubmit = useCallback(async () => {
        if (formRefs.length === 0) {
            return false;
        }

        const responseInput = [];
        let hasErrors = false;

        for (let i = 0; i < formRefs.length; i++) {
            const formRef = formRefs[i];
            const { values, errors } = await formRef.current.getValidatedValues(true);
            const errorKeys = Object.keys(errors);
            if (errorKeys.length > 0) {
                for (const key of errorKeys) {
                    applyFormErrorsRecursively(key, errors, formRef.current.setError, formRef.current.clearErrors);
                }

                hasErrors = true;
                continue;
            }

            responseInput.push({
                requisitionVendorQuoteId: values.requisitionVendorQuoteId,
                isAcceptable: values.isAcceptable,
                engineerComments: values.engineeringComments,
            })
        };

        if (hasErrors) return false;

        return {
            engineerApprovalInput: responseInput,
        }
    }, [formRefs]);
    let title = '';
    let subtitle = '';
    if (requisitionStatus === RequisitionStatus.CONTRACTING_REVIEW) {
        vendorQuotesSubmit.current = handleContractingReviewSubmit;
        title = 'Provide vendor responses and quotes';
    } else if (requisitionStatus === RequisitionStatus.ENGINEERING_APPROVAL) {
        vendorQuotesSubmit.current = handleEngineeringApprovalSubmit;
        title = 'Review vendor bids, and provide technical acceptance for each vendor';
        subtitle= 'Evaluate each vendor bid as “acceptable” or “unacceptable” and provide additional comments. “Unacceptable” evaluations require an explanation.'
    } else if(requisitionStatus === RequisitionStatus.CONTRACTING_APPROVAL){
        title = 'Review vendor bids and engineer evaluation, then submit to ordering admin';
        subtitle= 'You may also return to the previous step to make further edits'
    }
    else {
        vendorQuotesSubmit.current = () => { }
    }


    if (loadingActiveSinContracts || loadingContractCostBreakdown) {
        return <Spinner />;
    }

    return (
        <div className='contracting-review-actions'>
            <div className='contracting-review-actions-vendors-container'>
                <div className='blue-header'>current step actions</div>
                <div>
                    {title && (<h3>{title}</h3>)}
                    {subtitle && (<p>{subtitle}</p>)}
                </div>
                <div className='contracting-review-actions-vendor-container'>
                    {requisitionStatus === RequisitionStatus.CONTRACTING_REVIEW && (isAdmin || isContractingOfficer || isOrderingAdmin) && (
                        <div className='add-action'>
                            <Button
                                type='button'
                                label='Add vendor'
                                leftIcon={{ name: 'plus' }}
                                onClick={handleAddVendorQuote}
                                disabled={loadingCreateVendorQuote}
                            />
                        </div>
                    )}
                    <div className='contracting-review-actions-vendors'>
                        {vendorQuotes.map((vendorQuote, index) => (
                            <div className="contracting-review-actions-vendor-form-container">
                                <div className='contracting-review-actions-vendor-form-header'>
                                    <h3>Vendor {indexToAlphabet(index)}</h3>
                                    {index > 0 && requisitionStatus === RequisitionStatus.CONTRACTING_REVIEW  && (
                                        <div>
                                        <Button
                                            variant='unstyled'
                                            label='Delete this vendor'
                                            onClick={() => handleRemoveVendorQuote(vendorQuote.requisitionVendorQuoteId)}
                                            disabled={loadingDeleteVendorQuote}
                                        />
                                        </div>
                                    )}

                                    {(requisitionStatus === RequisitionStatus.ORDERING_ADMIN_APPROVAL || requisitionStatus === RequisitionStatus.PENDING_CUSTOMER_RESPONSE) && (
                                        <div>
                                            <Button
                                                label='Select vendor and complete requisition'
                                                type='button'
                                                rightIcon={{ name: 'arrow_forward' }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <ContractingReviewVendor
                                    key={vendorQuote.requisitionVendorQuoteId}
                                    vendorQuote={vendorQuote}
                                    index={index}
                                    attachmentTypes={attachmentTypes}
                                    vendors={vendors || []}
                                    makeModels={makeModelOptionMap || {}}
                                    formRef={formRefs[index]}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}